import React, { Component } from "react";
import { cloneDeep, debounce } from "lodash";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Alert,
} from "antd";
import {
  BankOutlined,
  BarcodeOutlined,
  DollarCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";

import Resources from "../scripts/resources";
import Rules from "../scripts/rules";
import API from "../scripts/api";
import { ProductUnits } from "../scripts/settings";

class ProductForm extends Component {
  constructor() {
    super();

    this.state = {
      isEdit: false,

      // وضعیت محصول
      isExistingProduct: false,
      selectedProductId: null,
      typedKeyword: "",

      // نتایج سرچ
      searchedProducts: [],
      searchLoading: false,
    };

    this.formRef = React.createRef();
    this.debouncedSearch = debounce(this.fetchProducts, 400);
  }

  handleChangeCategory = () => {
    this.formRef.current.setFieldsValue({ subCategory: undefined });
  };

  // -----------------------------
  // جستجوی سروری محصولات
  // -----------------------------
  fetchProducts = (keyword) => {
    const q = (keyword || "").trim();

    if (q.length < 2) {
      this.setState({ searchedProducts: [], searchLoading: false });
      return;
    }

    this.setState({ searchLoading: true });

    API.product
      .search({ q })
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? res ?? [];
        this.setState({
          searchedProducts: Array.isArray(data) ? data : [],
          searchLoading: false,
        });
      })
      .catch(() => {
        this.setState({ searchedProducts: [], searchLoading: false });
      });
  };

  // تایپ در فیلد سرچ
  handleSearch = (keyword) => {
    this.setState({ typedKeyword: keyword });
    this.debouncedSearch(keyword);
  };

  // -----------------------------
  // انتخاب آیتم از سلکت (کالای موجود یا کالای جدید)
  // -----------------------------
  handleSelectProduct = (value, option) => {
    // سناریو ۱: کاربر روی یکی از کالاهای پیدا شده کلیک کرد
    if (option && option.itemData) {
      const selected = option.itemData;

      this.setState({
        isExistingProduct: true,
        selectedProductId: selected.id,
        typedKeyword: selected.name || selected.title,
      });

      this.formRef.current.setFieldsValue({
        productName: selected.name || selected.title,
        code: selected.code,
        category: selected.category ? selected.category.id : undefined,
        subCategory: selected.sub_category ? selected.sub_category.id : undefined,
        unit: selected.unit,
        description: selected.description,
        tags: selected.tag ? selected.tag.map((t) => t.id) : [],
        packages: selected.package ? selected.package.map((p) => p.id) : [],
        inventory: undefined,
        priceOne: undefined,
      });
      return;
    }

    // سناریو ۲: کالا جدید است (تایپ شده توسط خود کاربر)
    this.setState({
      isExistingProduct: false,
      selectedProductId: null,
      typedKeyword: value,
    });

    this.formRef.current.setFieldsValue({
      productName: value,
      code: undefined,
    });
  };

  // در صورت پاک کردن فیلد
  handleClear = () => {
    this.setState({
      isExistingProduct: false,
      selectedProductId: null,
      typedKeyword: "",
      searchedProducts: [],
    });
    this.formRef.current.resetFields();
  };

  // -----------------------------
  // Submit
  // -----------------------------
  handleSubmit = () => {
    this.formRef.current.validateFields().then((values) => {
      const formData = new FormData();

      const rawPriceOne =
        values.priceOne !== undefined && values.priceOne !== null
          ? String(values.priceOne).replace(/,/g, "")
          : "";

      formData.append("inventory", values.inventory);
      formData.append("priceOne", rawPriceOne);

      // الف) اگر کالا از کاتالوگ انتخاب شده است:
      if (this.state.isExistingProduct && this.state.selectedProductId) {
        formData.append("product_id", this.state.selectedProductId);
        formData.append("is_existing", "1");
        this.props.onSubmit(formData);
        return;
      }

      // ب) اگر کالای کاملاً جدید ثبت می‌شود:
      formData.append("name", values.productName || this.state.typedKeyword);
      formData.append("unit", values.unit ?? "");

      if (values.tags?.length) values.tags.forEach((o) => formData.append("tags[]", o));
      if (values.packages?.length) values.packages.forEach((o) => formData.append("packages[]", o));
      if (values.category) formData.append("category", values.category);
      if (values.subCategory) formData.append("subCategory", values.subCategory);
      if (values.description) formData.append("description", values.description);

      this.props.onSubmit(formData);
    });
  };

  // -----------------------------
  // DidMount / Edit mode
  // -----------------------------
  handleDidMount = (data = {}) => {
    const isEdit = !!(data && data.id);
    this.setState({
      isEdit,
      isExistingProduct: false,
      selectedProductId: null,
      searchedProducts: [],
      typedKeyword: data?.name || "",
    });

    const cloneData = cloneDeep(data);
    cloneData.productName = cloneData.name || "";
    cloneData.tags = [];
    cloneData.packages = [];

    if (cloneData.tag && cloneData.tag.length) {
      cloneData.tag.forEach((o) => cloneData.tags.push(o.id));
    }
    if (cloneData.package && cloneData.package.length) {
      cloneData.package.forEach((o) => cloneData.packages.push(o.id));
    }

    cloneData.category = cloneData.category ? cloneData.category.id : undefined;
    cloneData.subCategory = cloneData.sub_category ? cloneData.sub_category.id : undefined;

    this.formRef.current.setFieldsValue(cloneData);
  };

    handleWillUnmount = () => {
    this.formRef.current.resetFields();
  };


  render() {
    const { mainCats, onCancel, subCats, submitting, tags, visible, packages } = this.props;

    const {
      isEdit,
      isExistingProduct,
      searchedProducts,
      searchLoading,
      typedKeyword,
    } = this.state;

    const tagOptions = (tags || []).map((o) => ({ label: o.name, value: o.id }));
    const packageOptions = (packages || []).map((o) => ({
      label: o.title,
      value: o.id,
      name: o.title,
    }));

    const cleanKeyword = (typedKeyword || "").trim();

    return (
      <Modal
        title={isEdit ? Resources.edit : "ثبت و تعیین قیمت محصول"}
        visible={visible}
        onOk={this.handleSubmit}
        onCancel={onCancel}
        okText={Resources.confirm}
        cancelText={Resources.cancel}
        okButtonProps={{ loading: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        width={720}
      >
        <Form ref={this.formRef} layout="vertical">
          <Row gutter={20}>
            {/* فیلد نام کالا: جستجو یا ایجاد جدید */}
            <Col xs={24}>
              <Form.Item
                name="productName"
                label="نام محصول (جستجو در کاتالوگ یا ثبت جدید)"
                rules={Rules.required}
              >
                {isEdit ? (
                  <Input readOnly />
                ) : (
                  <Select
                    showSearch
                    allowClear
                    placeholder="نام محصول را تایپ کنید..."
                    filterOption={false}
                    onSearch={this.handleSearch}
                    onSelect={this.handleSelectProduct}
                    onClear={this.handleClear}
                    loading={searchLoading}
                    notFoundContent={
                      searchLoading ? (
                        <div style={{ textAlign: "center", padding: 10 }}>
                          <Spin size="small" />
                        </div>
                      ) : null
                    }
                  >
                    {/* اگر کاربر تایپ کرده باشد، گزینه ایجاد محصول جدید همیشه اول لیست می‌آید */}
                    {cleanKeyword && (
                      <Select.Option key={`new_${cleanKeyword}`} value={cleanKeyword}>
                        <div style={{ color: "#1890ff", fontWeight: 500 }}>
                          <PlusCircleOutlined style={{ marginLeft: 6 }} />
                          ثبت به عنوان محصول جدید: <strong>"{cleanKeyword}"</strong>
                        </div>
                      </Select.Option>
                    )}

                    {/* لیست نتایج پیدا شده در کاتالوگ */}
                    {searchedProducts.map((p) => (
                      <Select.Option
                        key={p.id}
                        value={p.name || p.title}
                        itemData={p}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>{p.name || p.title}</span>
                          <span style={{ color: "#52c41a", fontSize: "12px" }}>
                            [موجود در کاتالوگ {p.code ? `- کد: ${p.code}` : ""}]
                          </span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>

              {/* اطلاع‌رسانی وضعیت کالا */}
              {isExistingProduct ? (
                <Alert
                  type="success"
                  showIcon
                  message="این کالا از قبل در سیستم موجود است. مشخصات کاتالوگ قفل شد؛ فقط قیمت و موجودی خود را وارد کنید."
                  style={{ marginBottom: 16 }}
                />
              ) : (
                !isEdit && (
                  <Alert
                    type="info"
                    showIcon
                    message="در صورتی که کالا جدید باشد، فیلدهای دسته‌بندی و مشخصات را تکمیل نمایید."
                    style={{ marginBottom: 16 }}
                  />
                )
              )}
            </Col>

            {/* بارکد در حالت ادیت */}
            {isEdit && (
              <Col xs={24} md={8}>
                <Form.Item name="code" label={Resources.barcode}>
                  <Input type="text" prefix={<BarcodeOutlined />} dir="ltr" readOnly />
                </Form.Item>
              </Col>
            )}

            {/* موجودی */}
            <Col xs={24} md={12}>
              <Form.Item name="inventory" label={Resources.inventory} rules={Rules.required}>
                <InputNumber
                  min={0}
                  prefix={<BankOutlined />}
                  dir="ltr"
                  style={{ width: "100%" }}
                  placeholder="تعداد موجودی"
                />
              </Form.Item>
            </Col>

            {/* قیمت */}
            <Col xs={24} md={12}>
              <Form.Item name="priceOne" label={Resources.price} rules={Rules.required}>
                <InputNumber
                  prefix={<DollarCircleOutlined />}
                  dir="ltr"
                  style={{ width: "100%" }}
                  placeholder="قیمت (تومان)"
                  formatter={(value) => {
                    if (value === undefined || value === null || value === "") return "";
                    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                  }}
                  parser={(value) => {
                    if (!value) return "";
                    return value.replace(/\$\s?|(,*)/g, "");
                  }}
                />
              </Form.Item>
            </Col>

            {/* دسته‌بندی اصلی */}
            <Col xs={24} md={12}>
              <Form.Item name="category" label={`${Resources.category} ${Resources.main}`}>
                <Select
                  filterOption
                  onChange={this.handleChangeCategory}
                  placeholder={Resources.choose}
                  optionFilterProp="name"
                  style={{ width: "100%" }}
                  showSearch
                  disabled={isExistingProduct}
                >
                  {mainCats.map((cat) => (
                    <Select.Option key={cat.id} value={cat.id} name={cat.name}>
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* زیردسته */}
            <Col xs={24} md={12}>
              <Form.Item
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.category !== currentValues.category
                }
                noStyle
              >
                {({ getFieldValue }) => (
                  <Form.Item name="subCategory" label={`${Resources.category} ${Resources.sub}`}>
                    <Select
                      filterOption
                      placeholder={Resources.choose}
                      optionFilterProp="name"
                      style={{ width: "100%" }}
                      showSearch
                      disabled={isExistingProduct}
                    >
                      {subCats
                        .filter((o) => parseInt(o.subCategory) === getFieldValue("category"))
                        .map((cat) => (
                          <Select.Option key={cat.id} value={cat.id} name={cat.name}>
                            {cat.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}
              </Form.Item>
            </Col>

            {/* واحد کالا */}
            <Col md={12} xs={24}>
              <Form.Item name="unit" label={Resources.unit}>
                <Select
                  optionFilterProp="name"
                  options={ProductUnits}
                  style={{ width: "100%" }}
                  disabled={isExistingProduct}
                />
              </Form.Item>
            </Col>

            {/* بسته‌بندی */}
            <Col md={12} xs={24}>
              <Form.Item name="packages" label={Resources.package}>
                <Select
                  mode="multiple"
                  optionFilterProp="name"
                  options={packageOptions}
                  style={{ width: "100%" }}
                  disabled={isExistingProduct}
                />
              </Form.Item>
            </Col>

            {/* تگ‌ها */}
            <Col xs={24}>
              <Form.Item name="tags" label={Resources.tags}>
                <Select
                  mode="multiple"
                  options={tagOptions}
                  style={{ width: "100%" }}
                  disabled={isExistingProduct}
                />
              </Form.Item>
            </Col>

            {/* توضیحات */}
            <Col xs={24}>
              <Form.Item name="description" label={Resources.description}>
                <Input.TextArea rows={3} disabled={isExistingProduct} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    );
  }
}

export default ProductForm;
